/**
 * Declined payments, for the Payments dashboard.
 *
 * The chart above this one notes that the gap between invoiced and collected
 * "is failed cards and dunning" and that it is invisible. This is that gap,
 * named.
 *
 * Three things are kept apart on purpose, because each has a different fix and
 * merging them produces a big number that nobody can act on:
 *
 *   Declined            A card was presented and refused.
 *   Checkout abandoned  The page loaded and no card was ever entered. Usually
 *                       several times the decline count. Nothing was refused.
 *   Renewal vs checkout A failed rebill is involuntary churn; a failed signup
 *                       is a lost prospect.
 *
 * AMOUNTS ARRIVE IN CENTS. The page's formatCurrency takes DOLLARS — unlike
 * the identically-named helper on the Refunds page, which takes cents — so
 * every amount here is divided before it is formatted. Getting this backwards
 * renders every figure at 1% of its true value, which looks plausible.
 */

import {
  BarChart, Bar, LineChart, Line, Brush, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { XCircle, UserX, RotateCcw, ShoppingCart, CreditCard, Repeat } from 'lucide-react'

import { Card, CardHeader, CardTitle, CardContent } from './ui'

/** Category → colour. Consistent everywhere a reason is drawn. */
const CATEGORY_STYLE = {
  processor: { fill: '#ef4444', label: 'Processor declines' },
  customer: { fill: '#8b5cf6', label: 'Customer declines' },
  unknown: { fill: '#9ca3af', label: 'Unclassified' },
}

const money = (cents, format) => format((Number(cents) || 0) / 100)
const pct = value => (typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : '—')

/**
 * One headline number.
 *
 * `tone` colours only the value, never the card: a decline rate is not an
 * error state, and painting the whole tile red would make a normal 30% read
 * like an outage.
 */
function Tile({ label, value, sub, icon: Icon, tone = '' }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-muted-foreground">{label}</span>
          {Icon && (
            <div className="p-2 bg-muted rounded-lg">
              <Icon className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className={`text-2xl font-bold tracking-tight ${tone}`}>{value}</div>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  )
}

/**
 * Reasons as a ranked list with proportional bars.
 *
 * A list rather than a donut: there are usually a dozen reasons with a long
 * tail, and a donut of twelve wedges is unreadable at any size. The bar is
 * scaled to the LARGEST reason, not to the total, so the small ones stay
 * visible instead of collapsing to a hairline.
 */
function ReasonBars({ reasons, formatCurrency }) {
  if (!reasons?.length) return null
  const max = Math.max(...reasons.map(r => r.count))

  return (
    <div className="space-y-2.5">
      {reasons.map(reason => {
        const style = CATEGORY_STYLE[reason.declineType] || CATEGORY_STYLE.unknown
        return (
          <div key={reason.key}>
            <div className="flex items-baseline justify-between text-sm gap-2">
              <span className="truncate" title={reason.detail ? `${reason.name} — ${reason.detail}` : reason.name}>
                {reason.name}
                {/* Authorize.net gives several codes the same message, so the
                    code is what tells two "declined" rows apart. */}
                {reason.key?.startsWith('authorizedotnet:') && (
                  <span className="ml-1.5 font-mono text-xs text-muted-foreground">{reason.code}</span>
                )}
              </span>
              <span className="tabular-nums flex-shrink-0 text-muted-foreground">
                {reason.count} · {money(reason.amount, formatCurrency)}
              </span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  // A floor, so a single decline is still a visible mark
                  // rather than a sliver indistinguishable from zero.
                  width: `${Math.max(1.5, (100 * reason.count) / max)}%`,
                  backgroundColor: style.fill,
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** 0 → "12a", 13 → "1p". */
const hourLabel = hour => `${hour % 12 || 12}${hour < 12 ? 'a' : 'p'}`
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
/** "2026-09-30" → "Sep 30". */
const dayLabel = day => `${MONTHS[Number(day.slice(5, 7)) - 1]} ${Number(day.slice(8, 10))}`

/**
 * Every hour of the range as its own point. Counts on one axis, approved and
 * declined as two lines; the decline rate is in the tooltip rather than on a
 * second axis, where the reader would compare the rate line's height with the
 * counts'. A rate per hour is also too noisy to plot on low volume — one
 * decline in a two-attempt hour reads as 50%.
 */
function HourlyTooltip({ active, payload, formatNumber }) {
  const row = active && payload?.[0]?.payload
  if (!row) return null
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-sm">
      <div className="font-medium mb-1">{dayLabel(row.day)}, {hourLabel(row.hourOfDay)}–{hourLabel((row.hourOfDay + 1) % 24)}</div>
      <div>{formatNumber(row.Declined)} declined of {formatNumber(row.attempted)} attempts</div>
      <div className="text-muted-foreground">Decline rate {pct(row.declineRate)}</div>
      {row.renewalDeclined > 0 && (
        <div className="text-muted-foreground">{formatNumber(row.renewalDeclined)} were renewals</div>
      )}
    </div>
  )
}

/**
 * Absolute acceptance rate over the last 6, 12 and 24 hours — people who paid
 * ÷ people who attempted — anchored at NOW rather than the date range, so a
 * past day still shows whether people are getting through right now.
 * Rendered even when the range itself has no attempts.
 */
function TrailingRates({ data, formatNumber }) {
  const windows = data.trailing?.windows || []
  if (!windows.length) return null
  const asOf = data.trailing.asOf
    ? new Date(data.trailing.asOf).toLocaleTimeString('en-US', { timeZone: data.timezone || 'America/New_York', hour: 'numeric', minute: '2-digit' })
    : null
  return (
    <section aria-label="Acceptance right now" className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Right now{asOf ? `, as of ${asOf} ET` : ''}. These ignore the date range{data.mid ? ` but follow the ${data.mid.name} filter` : ''}.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {windows.map(row => (
          <Tile
            key={row.hours}
            label={`Acceptance, last ${row.hours} hours`}
            value={pct(row.acceptanceRate)}
            sub={<>
              {row.peopleAttempted > 0
                ? `${formatNumber(row.peoplePaid)} of ${formatNumber(row.peopleAttempted)} people paid a first payment`
                : 'No first payments attempted'}
              {row.renewal && (
                <span className="block">
                  Renewals: {pct(row.renewal.acceptanceRate)}
                  {row.renewal.peopleAttempted > 0 ? ` (${formatNumber(row.renewal.peoplePaid)} of ${formatNumber(row.renewal.peopleAttempted)})` : ''}
                </span>
              )}
            </>}
          />
        ))}
      </div>
    </section>
  )
}

export default function DeclinesSection({ data, loading, error, formatCurrency, formatNumber }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground text-sm">
          Loading declines…
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          Declines could not be loaded. {error}
        </CardContent>
      </Card>
    )
  }

  if (!data) return null

  const { totals, recovery, abandoned, byReason, byInitiation, series, window } = data

  // A tenant with no attempts at all in the window has nothing to say here,
  // and four tiles of "—" say it worse than one sentence.
  if (!totals?.attempted) {
    return (
      <div className="space-y-4">
      <TrailingRates data={data} formatNumber={formatNumber} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5" />
            Declines
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          No payments were attempted in the last {window?.days ?? 30} days.
        </CardContent>
      </Card>
      </div>
    )
  }

  const chartData = (series || []).map(day => ({
    date: day.date?.slice(5) || '',
    Declined: day.declined,
    Approved: day.succeeded,
  }))

  // `date` is "YYYY-MM-DD HH:00" ET.
  const hourlyData = (data.hourlySeries || []).map(bucket => ({
    ...bucket,
    day: bucket.date.slice(0, 10),
    hourOfDay: Number(bucket.date.slice(11, 13)),
    Declined: bucket.declined,
    Approved: bucket.succeeded,
  }))
  // One tick per day, at its first hour.
  const dayTicks = hourlyData.filter((row, i) => i === 0 || row.day !== hourlyData[i - 1].day).map(row => row.date)
  const hasHourly = hourlyData.some(bucket => bucket.attempted > 0)

  const renewals = byInitiation?.renewal?.count || 0
  const checkouts = byInitiation?.checkout?.count || 0

  return (
    <div className="space-y-4">
      <TrailingRates data={data} formatNumber={formatNumber} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label="Unique declines" value={formatNumber(totals.uniqueDeclines)} sub="People with declines and no successful payment in this range" icon={UserX} />
        <Tile label="Total declines" value={formatNumber(totals.declined)} sub="Every failed payment, including retries by people who paid" icon={XCircle} />
        <Tile label="Acceptance rate" value={pct(totals.absoluteAcceptanceRate)} sub={`First payments: ${formatNumber(totals.uniquePaid)} paid ÷ ${formatNumber(totals.uniqueAttempted)} people who attempted`} icon={CreditCard} />
        <Tile label="Renewal acceptance rate" value={pct(totals.renewalAcceptanceRate)} sub={totals.renewalUniqueAttempted > 0
          ? `Renewals: ${formatNumber(totals.renewalUniquePaid)} paid ÷ ${formatNumber(totals.renewalUniqueAttempted)} subscribers charged`
          : 'No renewals charged in this range'} icon={Repeat} />
      </div>
      <p className="text-xs text-muted-foreground">People are grouped by email within this report’s dates. Acceptance counts first payments only; renewals — a card on file charged on schedule — have their own rate. Three declines followed by one successful payment count as 0 unique declines, 3 total declines, and 100% acceptance. {totals.attemptsWithoutEmail > 0 && `${formatNumber(totals.attemptsWithoutEmail)} attempts have no email and are excluded from people-based metrics.`}</p>
      <Card>
        <CardHeader><CardTitle>Processor vs customer declines</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm tabular-nums">
            <thead><tr><th scope="col" className="py-2 text-left">Type</th><th scope="col" className="py-2 text-right">Total declines</th><th scope="col" className="py-2 text-right">Unique declines</th></tr></thead>
            <tbody>{(data.byDeclineType || []).map(row => <tr key={row.name} className="border-t"><th scope="row" className="py-2 text-left font-normal">{CATEGORY_STYLE[row.name]?.label || row.name}</th><td className="py-2 text-right">{formatNumber(row.count)}</td><td className="py-2 text-right">{formatNumber(row.uniqueDeclines)}</td></tr>)}</tbody>
          </table>
          <p className="mt-3 text-xs text-muted-foreground">Insufficient funds, invalid or expired cards, a wrong card number, expiry, CVC or billing address, and incomplete authentication are customer declines. Do not honor, processor errors, and merchant configuration issues are processor declines. Unknown codes stay unclassified. Each unpaid person is assigned to their latest decline type, so unique counts do not overlap.</p>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile
          label="Attempt decline rate"
          value={pct(totals.declineRate)}
          sub={`${formatNumber(totals.declined)} of ${formatNumber(totals.attempted)} attempts refused`}
          icon={XCircle}
          tone={totals.declineRate > 0.25 ? 'text-red-600 dark:text-red-400' : ''}
        />
        <Tile
          label="Unrecovered amount"
          value={money(recovery.lostAmount, formatCurrency)}
          // The number people reach for is the decline count; it overstates
          // the damage by about half, because most declines are retries by
          // someone who eventually paid. This is what actually walked away.
          sub="Largest declined amount per unpaid person"
          icon={UserX}
          tone={recovery.lost > 0 ? 'text-red-600 dark:text-red-400' : ''}
        />
        <Tile
          label="Recovered"
          value={pct(recovery.recoveryRate)}
          sub={`${formatNumber(recovery.recovered)} of ${formatNumber(recovery.people)} declined customers went on to pay`}
          icon={RotateCcw}
          tone={recovery.recovered > 0 ? 'text-emerald-600 dark:text-emerald-400' : ''}
        />
        <Tile
          label="Checkout abandoned"
          // Null when the report is narrowed to one MID: no card was entered,
          // so no merchant account was ever chosen.
          value={abandoned ? formatNumber(abandoned.count) : '—'}
          // Deliberately not added to declines: no card was entered, so
          // nothing was refused. It is a funnel number, not a payments one.
          sub={abandoned ? 'Reached checkout, never entered a card' : 'Not tied to a MID: no card was entered'}
          icon={ShoppingCart}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Approved vs declined attempts
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Last {window?.days ?? 30} days, {data.timezone?.split('/')[1]?.replace('_', ' ') || 'Eastern'} time.
            </p>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 12 }}
                      wrapperClassName="!bg-popover !border-border rounded-lg"
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {/* Stacked, so the column height is total demand and the
                        red share is what was lost out of it. Two side-by-side
                        bars would make the reader do that division. */}
                    <Bar dataKey="Approved" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Declined" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center text-sm text-muted-foreground">
                Nothing in this range.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Why payments were refused</CardTitle>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {Object.entries(CATEGORY_STYLE).map(([key, style]) => (
                <span key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: style.fill }} />
                  {style.label}
                </span>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <ReasonBars reasons={byReason} formatCurrency={formatCurrency} />
          </CardContent>
        </Card>
      </div>

      {hasHourly && (
        <Card>
          <CardHeader>
            <CardTitle>Declines per hour</CardTitle>
            <p className="text-xs text-muted-foreground">
              Every hour in the range, {data.timezone?.split('/')[1]?.replace('_', ' ') || 'Eastern'} time.
              Drag the handles under the chart to zoom in; hover an hour for its decline rate.
            </p>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    ticks={dayTicks}
                    tickFormatter={value => dayLabel(value.slice(0, 10))}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={24}
                  />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} width={32} />
                  <Tooltip content={<HourlyTooltip formatNumber={formatNumber} />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {/* Same colours as the daily chart above. */}
                  <Line type="linear" dataKey="Approved" stroke="#10b981" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Line type="linear" dataKey="Declined" stroke="#ef4444" strokeWidth={2} dot={false} isAnimationActive={false} />
                  <Brush dataKey="date" height={24} travellerWidth={8} tickFormatter={value => dayLabel(value.slice(0, 10))} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* The renewal/checkout split, only where both exist. A tenant selling
          prepaid multi-month plans has no renewals yet, and a row reading
          "0 renewals" invites the reading that rebills are failing to run. */}
      {renewals > 0 && (
        <Card>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground">Failed renewals</div>
                <div className="text-2xl font-bold tracking-tight mt-1">{formatNumber(renewals)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Rebills of existing subscribers — {money(byInitiation.renewal.amount, formatCurrency)}.
                  This is involuntary churn, and dunning is what recovers it.
                </p>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Failed checkouts</div>
                <div className="text-2xl font-bold tracking-tight mt-1">{formatNumber(checkouts)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  New customers whose card was refused at signup —{' '}
                  {money(byInitiation.checkout.amount, formatCurrency)}.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-muted-foreground space-y-1">
        <p>
          A <strong>decline</strong> is a payment that was presented and refused. Checkout
          abandonment is counted separately and never added in: those are intents that
          reached the payment page and never received a card, so nothing was refused.
        </p>
        <p>
          <strong>Unique declines</strong> counts people, not attempts. Retries are grouped by
          email address — the payment processor issues a new customer id on every attempt, so
          one person retrying three times appears as three customers to anything that groups
          the other way. Someone with any successful payment in this range counts as
          recovered; the amount shown as never collected is the largest single attempt each
          lost customer made, not the sum of their retries.
          {recovery.declinesWithoutEmail > 0 && (
            <>
              {' '}{formatNumber(recovery.declinesWithoutEmail)}{' '}
              {recovery.declinesWithoutEmail === 1 ? 'decline carries' : 'declines carry'} no email
              address and {recovery.declinesWithoutEmail === 1 ? 'is' : 'are'} outside these
              figures entirely.
            </>
          )}
        </p>
        <p>
          Reasons come from the processor's own code rather than the payment gateway's
          normalised one, which is not reliable — it files the same issuer response under
          different categories on different rows. Each brand's processor uses its own
          vocabulary; unrecognised codes are shown as themselves rather than guessed into a
          bucket. <strong>Do not honor</strong> is an issuer declining without stating a
          reason, and is not a fraud signal.
        </p>
      </div>
    </div>
  )
}
